/* ==========================================================
   REVIEW DYNAMIC CAROUSEL — behavior
   - Detects whichever slide is closest to the viewport center
     and marks it "active" so CSS can zoom it into focus.
   - Clicking any card (that isn't already active) scrolls it
     into the center.
   - Prev/next arrows, optional dots, optional autoplay, all
     via native scroll-snap (no external library).
   ========================================================== */

(function () {
  'use strict';

  function initCarousel(section) {
    var viewport = section.querySelector('[data-icf3-viewport]');
    var track = section.querySelector('[data-icf3-track]');
    var dotsWrap = section.querySelector('[data-icf3-dots]');
    var prevBtn = section.querySelector('[data-icf3-prev]');
    var nextBtn = section.querySelector('[data-icf3-next]');
    if (!viewport || !track) return;

    var slides = Array.prototype.slice.call(track.children);
    if (!slides.length) return;

    var zoomEnabled = section.dataset.zoomEnabled === 'true';
    var autoplay = section.dataset.autoplay === 'true';
    var autoplaySpeed = parseInt(section.dataset.autoplaySpeed, 10) || 5000;
    var autoplayTimer = null;

    function updateActiveSlide() {
      if (!zoomEnabled) return;
      var viewportCenter = viewport.scrollLeft + viewport.clientWidth / 2;
      var closestIndex = 0;
      var closestDistance = Infinity;

      slides.forEach(function (slide, i) {
        var slideCenter = slide.offsetLeft + slide.offsetWidth / 2;
        var distance = Math.abs(slideCenter - viewportCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestIndex = i;
        }
      });

      slides.forEach(function (slide, i) {
        slide.classList.toggle('icf3__slide--active', i === closestIndex);
      });

      return closestIndex;
    }

    function stepWidth() {
      var firstRect = slides[0].getBoundingClientRect();
      var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 0);
      return firstRect.width + gap;
    }

    function pageCount() {
      var step = stepWidth();
      if (!step) return 1;
      var visible = viewport.clientWidth / step;
      return Math.max(1, Math.ceil(slides.length - visible) + 1);
    }

    function currentIndex() {
      var step = stepWidth();
      if (!step) return 0;
      return Math.round(viewport.scrollLeft / step);
    }

    function goToIndex(index) {
      var step = stepWidth();
      var maxScroll = track.scrollWidth - viewport.clientWidth;
      var target = Math.max(0, Math.min(index * step, maxScroll));
      viewport.scrollTo({ left: target, behavior: 'smooth' });
    }

    function goToSlide(slideEl) {
      var index = slides.indexOf(slideEl);
      if (index === -1) return;
      var slideCenter = slideEl.offsetLeft + slideEl.offsetWidth / 2;
      var target = slideCenter - viewport.clientWidth / 2;
      var maxScroll = track.scrollWidth - viewport.clientWidth;
      viewport.scrollTo({
        left: Math.max(0, Math.min(target, maxScroll)),
        behavior: 'smooth'
      });
    }

    function buildDots() {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';
      var pages = pageCount();
      for (var i = 0; i < pages; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'icf3__dot';
        dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
        dot.setAttribute('aria-current', i === 0 ? 'true' : 'false');
        (function (index) {
          dot.addEventListener('click', function () {
            goToIndex(index);
          });
        })(i);
        dotsWrap.appendChild(dot);
      }
    }

    function updateDots() {
      if (!dotsWrap) return;
      var dots = dotsWrap.querySelectorAll('.icf3__dot');
      var index = Math.min(currentIndex(), dots.length - 1);
      dots.forEach(function (d, i) {
        d.setAttribute('aria-current', i === index ? 'true' : 'false');
      });
    }

    function updateArrows() {
      if (!prevBtn && !nextBtn) return;
      var maxScroll = track.scrollWidth - viewport.clientWidth - 2;
      if (prevBtn) prevBtn.toggleAttribute('disabled', viewport.scrollLeft <= 0);
      if (nextBtn) nextBtn.toggleAttribute('disabled', viewport.scrollLeft >= maxScroll);
    }

    function goNext() {
      var pages = pageCount();
      var next = currentIndex() + 1;
      if (next >= pages) {
        goToIndex(0);
      } else {
        goToIndex(next);
      }
    }

    function goPrev() {
      goToIndex(currentIndex() - 1);
    }

    var scrollTimer = null;
    viewport.addEventListener('scroll', function () {
      if (scrollTimer) window.cancelAnimationFrame(scrollTimer);
      scrollTimer = window.requestAnimationFrame(function () {
        updateActiveSlide();
        updateDots();
        updateArrows();
      });
    });

    slides.forEach(function (slide) {
      var card = slide.querySelector('[data-icf3-card]');
      var video = slide.querySelector('.icf3-card__video');
      var productLink = slide.querySelector('[data-icf3-product-link]');

      function activateCard() {
        var isActive = slide.classList.contains('icf3__slide--active');
        if (!zoomEnabled || !isActive) {
          goToSlide(slide);
          return;
        }
        // Card is already centered/active: activating it toggles the
        // video instead of re-scrolling (matters most on touch,
        // where there's no hover to trigger a preview play).
        if (video) {
          if (video.paused) {
            video.play().catch(function () {});
          } else {
            video.pause();
          }
        }
      }

      if (card) {
        card.addEventListener('click', function (e) {
          // Let the "Product used" link behave like a normal link
          // (navigate) instead of triggering the card's own behavior.
          if (productLink && (e.target === productLink || productLink.contains(e.target))) {
            return;
          }
          e.preventDefault();
          activateCard();
        });

        // The card is a div with role="button", not a native <button>,
        // so Enter/Space activation has to be wired up manually.
        card.addEventListener('keydown', function (e) {
          if (e.target !== card) return; // don't hijack Enter on the nested product link
          if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
            e.preventDefault();
            activateCard();
          }
        });
      }

      if (productLink) {
        // Prevent the click from bubbling into the card's own click
        // handler above; the browser still follows the link normally.
        productLink.addEventListener('click', function (e) {
          e.stopPropagation();
        });
      }

      if (video) {
        var wrap = video.closest('.icf3-card__video-wrap');

        video.addEventListener('play', function () {
          if (wrap) wrap.classList.add('is-playing');
        });
        video.addEventListener('pause', function () {
          if (wrap) wrap.classList.remove('is-playing');
        });

        // Desktop: preview-play (muted) on hover, pause on leave.
        slide.addEventListener('mouseenter', function () {
          video.play().catch(function () {});
        });
        slide.addEventListener('mouseleave', function () {
          video.pause();
          video.currentTime = 0;
        });
      }
    });

    if (prevBtn) prevBtn.addEventListener('click', goPrev);
    if (nextBtn) nextBtn.addEventListener('click', goNext);

    function startAutoplay() {
      if (!autoplay) return;
      stopAutoplay();
      autoplayTimer = window.setInterval(goNext, autoplaySpeed);
    }
    function stopAutoplay() {
      if (autoplayTimer) {
        window.clearInterval(autoplayTimer);
        autoplayTimer = null;
      }
    }

    if (autoplay) {
      section.addEventListener('mouseenter', stopAutoplay);
      section.addEventListener('mouseleave', startAutoplay);
      section.addEventListener('touchstart', stopAutoplay, { passive: true });
      startAutoplay();
    }

    buildDots();
    updateActiveSlide();
    updateArrows();

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () {
        buildDots();
        updateActiveSlide();
        updateDots();
        updateArrows();
      }, 150);
    });
  }

  function initAll() {
    document.querySelectorAll('[data-icf3-section]').forEach(initCarousel);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target.querySelector('[data-icf3-section]');
    if (section) initCarousel(section);
  });

  document.addEventListener('shopify:section:reorder', initAll);
})();